<template>
  <div class="mtp">
    <!-- ═══════════ Galerie: eine Karte je Template ═══════════ -->
    <template v-if="view === 'grid'">
      <div class="mtp-toolbar">
        <div>
          <div class="mtp-toolbar__title">Mail-Templates</div>
          <div class="mtp-toolbar__sub">
            Kundenmails in {{ langItems.length }} Sprachen — Änderungen gelten sofort für alle künftigen Mails.
          </div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedStoreKey"
          :items="storeItems"
          outlined dense hide-details
          class="mtp-select mtp-select--store"
          menu-props="offsetY"
          prepend-inner-icon="mdi-storefront-outline"
          title="Store-Sicht für Vorschau und Testmail"
        />
      </div>

      <div v-if="!loading && !templateList.length" class="mtp-empty">
        Noch keine Templates in <code>transactionalMailTemplates</code>. Sie werden beim ersten Versand aus den Standards gesät.
      </div>

      <div class="mtp-gallery">
        <div v-for="tpl in templateList" :key="tpl.id" class="mtp-tplcard" :class="{ 'mtp-tplcard--incomplete': tpl.missingLangs > 0 }">
          <div class="mtp-tplcard__preview" @click="openEditor(tpl.id)">
            <iframe class="mtp-tplcard__frame" :srcdoc="cardHtml(tpl)" sandbox="" scrolling="no" tabindex="-1" :title="tpl.name" />
            <div class="mtp-tplcard__hover">
              <v-icon color="white">mdi-pencil</v-icon>
            </div>
          </div>
          <div class="mtp-tplcard__meta">
            <div class="mtp-tplcard__icon">
              <v-icon small>{{ tpl.icon }}</v-icon>
            </div>
            <div class="mtp-tplcard__text">
              <div class="mtp-tplcard__name">{{ tpl.name }}</div>
              <div class="mtp-tplcard__desc">{{ tpl.description }}</div>
              <div class="mtp-tplcard__badges">
                <span v-if="tpl.missingLangs === 0" class="mtp-badge mtp-badge--ok">{{ langItems.length }} Sprachen vollständig</span>
                <span v-else class="mtp-badge mtp-badge--warn">{{ tpl.missingLangs }} {{ tpl.missingLangs === 1 ? "Sprache" : "Sprachen" }} unvollständig</span>
                <span v-if="tpl.updatedAt" class="mtp-badge" :title="tpl.updatedAt">bearbeitet {{ shortDate(tpl.updatedAt) }}</span>
                <span v-else class="mtp-badge">Standardtexte</span>
              </div>
            </div>
          </div>
          <div class="mtp-tplcard__actions">
            <v-btn text small color="#7b5cd6" class="mtp-btn" :loading="sendingTestId === tpl.id" @click="sendTestMail(tpl.id)">
              <v-icon small left>mdi-email-check-outline</v-icon>
              Testmail
            </v-btn>
            <v-spacer />
            <v-btn text small color="#7b5cd6" class="mtp-btn" @click="openEditor(tpl.id)">
              Bearbeiten
              <v-icon small right>mdi-arrow-right</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══════════ Editor: Texte je Sprache + Vorschau ═══════════ -->
    <template v-else-if="template">
      <div class="mtp-toolbar">
        <v-btn icon class="mtp-back" title="Zurück zur Übersicht" @click="backToGrid">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="mtp-toolbar__icon">
          <v-icon small>{{ meta(selectedTemplateId).icon }}</v-icon>
        </div>
        <div>
          <div class="mtp-toolbar__title">{{ meta(selectedTemplateId).name }}</div>
          <div class="mtp-toolbar__sub">{{ meta(selectedTemplateId).description }}</div>
        </div>
        <v-spacer />
        <v-select
          v-model="selectedStoreKey"
          :items="storeItems"
          outlined dense hide-details
          class="mtp-select mtp-select--store"
          menu-props="offsetY"
          prepend-inner-icon="mdi-storefront-outline"
        />
        <v-text-field
          v-model="testOrderRef"
          outlined dense hide-details clearable
          class="mtp-select mtp-select--order"
          placeholder="#91798 (optional)"
          prepend-inner-icon="mdi-package-variant-closed"
          title="Testmail mit echter Bestellung: Sprache, Store, Kunde und Bilder kommen dann aus der Order"
        />
        <v-btn
          outlined rounded color="#7b5cd6"
          class="mtp-btn"
          :loading="sendingTestId === selectedTemplateId"
          @click="sendTestMail(selectedTemplateId)"
        >
          <v-icon small left>mdi-email-check-outline</v-icon>
          Testmail an mich
        </v-btn>
        <v-btn
          rounded depressed dark
          color="#7b5cd6"
          class="mtp-btn"
          :disabled="!isDirty"
          :loading="saving"
          @click="saveLang"
        >
          <v-icon small left>mdi-content-save-outline</v-icon>
          Speichern
        </v-btn>
      </div>

      <!-- Sprach-Pills mit Vollständigkeits-Ampel -->
      <div class="mtp-langs">
        <button
          v-for="l in langItems"
          :key="l.value"
          class="mtp-lang"
          :class="{ 'mtp-lang--active': selectedLang === l.value }"
          @click="switchLang(l.value)"
        >
          <span
            class="mtp-lang__dot"
            :class="completeness[l.value] === 0 ? 'mtp-lang__dot--ok' : 'mtp-lang__dot--missing'"
          />
          {{ l.text }}
          <span v-if="completeness[l.value] > 0" class="mtp-lang__count">{{ completeness[l.value] }}</span>
        </button>
      </div>

      <div class="mtp-grid">
        <v-card class="mtp-card">
          <div class="mtp-card__head">
            <span class="mtp-card__title">
              <v-icon small class="mtp-card__icon">mdi-translate</v-icon>
              Texte — {{ langLabel(selectedLang) }}
            </span>
            <span v-if="selectedLang !== 'de'" class="mtp-muted">Referenz darunter: Deutsch</span>
          </div>

          <div v-for="key in textKeys" :key="key" class="mtp-key">
            <div class="mtp-key__label">
              <code>{{ key }}</code>
              <span v-if="placeholderMismatch(key)" class="mtp-warn" title="Platzhalter weichen vom deutschen Original ab">
                <v-icon x-small color="#b26a00">mdi-code-braces</v-icon> Platzhalter prüfen
              </span>
            </div>
            <v-textarea
              v-model="draft[key]"
              :rows="rowsFor(key)"
              auto-grow dense outlined hide-details
              class="mtp-key__input"
              :class="{ 'mtp-key__input--empty': !String(draft[key] || '').trim() }"
            />
            <div v-if="selectedLang !== 'de'" class="mtp-key__reference">
              {{ deTexts[key] }}
            </div>
          </div>
        </v-card>

        <v-card class="mtp-card mtp-card--preview">
          <div class="mtp-card__head">
            <span class="mtp-card__title">
              <v-icon small class="mtp-card__icon">mdi-eye-outline</v-icon>
              Vorschau
            </span>
            <span class="mtp-subject" :title="previewSubject">{{ previewSubject }}</span>
          </div>
          <iframe
            class="mtp-preview"
            :srcdoc="previewHtml"
            sandbox=""
            title="Mail-Vorschau"
          />
        </v-card>
      </div>
    </template>

    <v-overlay :value="loading" opacity="0.15">
      <v-progress-circular indeterminate color="#7b5cd6" />
    </v-overlay>
  </div>
</template>

<script>
import { collection, doc, onSnapshot, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions as fbFunctions } from "../../../../fb";
import { EventBus, events } from "../../../../event-bus";

// Anzeige-Namen + Auslöser der Templates (IDs = transactionalMailTemplates-Doc-IDs). Die Reihenfolge hier ist
// die Reihenfolge der Karten; unbekannte Docs (z. B. neu gesät) hängen hinten an und nutzen name/description
// aus dem Doc, sonst die ID.
const TEMPLATE_META = {
  updates_artwork: { name: "Live-Vorschau / Refinement", description: "Kunde bekommt die Vorschau seines Kunstwerks zur Bewertung", icon: "mdi-image-edit-outline" },
  send_results_link: { name: "Ergebnis — Customizer", description: "Fertiges Kunstwerk aus dem Customizer, Link zum Ergebnis", icon: "mdi-check-decagram-outline" },
  send_results_digital: { name: "Ergebnis — Digital", description: "Digitale Bestellung fertig, Link zu den Downloads", icon: "mdi-download-outline" },
  send_results_link_preview: { name: "Gratis-Vorschau + Coupon", description: "Kostenlose Vorschau mit Rabattcode für die Bestellung", icon: "mdi-ticket-percent-outline" },
  action_invalid_image: { name: "Upload-Alternativen (Rahmen)", description: "Foto ungeeignet: Team-Text mit Link für alternative Uploads", icon: "mdi-image-off-outline" },
  order_edit_link: { name: "Änderungslink", description: "Kunde darf Bild, Text, Hintergrund, Größe oder Extras seiner Bestellung ändern", icon: "mdi-link-variant" },
  order_edit_gift_card: { name: "Gutschrift nach Änderung", description: "Gutschein-Code, wenn eine Bestelländerung günstiger wurde (2 Jahre gültig)", icon: "mdi-wallet-giftcard" },
};
const TEMPLATE_ORDER = Object.keys(TEMPLATE_META);

const LANGS = [
  { value: "de", text: "DE" },
  { value: "en", text: "EN" },
  { value: "fr", text: "FR" },
  { value: "es", text: "ES" },
  { value: "it", text: "IT" },
  { value: "nl", text: "NL" },
  { value: "pl", text: "PL" },
  { value: "pt", text: "PT" },
  { value: "da", text: "DA" },
  { value: "sv", text: "SV" },
  { value: "cs", text: "CS" },
  { value: "sl", text: "SL" },
  { value: "sk", text: "SK" },
];

const LANG_NAMES = {
  de: "Deutsch", en: "Englisch", fr: "Französisch", es: "Spanisch", it: "Italienisch",
  nl: "Niederländisch", pl: "Polnisch", pt: "Portugiesisch", da: "Dänisch",
  sv: "Schwedisch", cs: "Tschechisch", sl: "Slowenisch", sk: "Slowakisch",
};

// Store-Sicht für Vorschau/Testversand — Branding-Spiegel von
// shopBasedEnvVariables in functions/utils.js (DOMAIN/LOGO_URL/EMAIL_REPLY_TO).
// Bei Änderungen dort BEIDE Stellen pflegen.
const STORES = [
  { key: "AQ_DE", shopId: "AQ", countryCode: "DE", text: "Artquarell.com", defaultLang: "de",
    brandName: "Artquarell.com", supportEmail: "du@artquarell.com",
    logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600" },
  { key: "AQ_ES", shopId: "AQ", countryCode: "ES", text: "Artcuarela.es", defaultLang: "es",
    brandName: "Artcuarela.es", supportEmail: "lisa@artcuarela.es",
    logoUrl: "https://artcuarela.es/cdn/shop/files/logo_right_360x.png?v=1669124511" },
  { key: "AQ_FR", shopId: "AQ", countryCode: "FR", text: "Artquarelle.fr", defaultLang: "fr",
    brandName: "Artquarelle.fr", supportEmail: "support@artquarelle.fr",
    logoUrl: "https://artquarelle.fr/cdn/shop/files/logo_FR-min.png?v=1724933265&width=500" },
  { key: "AQ_PL", shopId: "AQ", countryCode: "PL", text: "Artkwarela.pl", defaultLang: "pl",
    brandName: "Artkwarela.pl", supportEmail: "ty@artkwarela.pl", logoUrl: "" },
  { key: "TM_DE", shopId: "TM", countryCode: "DE", text: "TierBeiMir.com", defaultLang: "de",
    brandName: "TierBeiMir.com", supportEmail: "lisa@tierbeimir.com", logoUrl: "" },
  { key: "OH_US", shopId: "OH", countryCode: "US", text: "OhMy.art", defaultLang: "en",
    brandName: "OhMy.art", supportEmail: "support@ohmy.art",
    logoUrl: "https://cdn.shopify.com/s/files/1/0895/9112/0219/files/ohmyart_loogo_large_fd739e6a-be5b-4e4a-9181-069174ebf16e_430x.png" },
];

// Beispieldaten für die Vorschau — Spiegel von SAMPLE_DATA in
// functions/artquarellMain/TransactionalMail/transactionalMail.js
const SAMPLE_DATA = {
  customerName: "Malte",
  orderName: "#TEST",
  resultUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  resultThumbnailUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  mockupUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  downloadsUrl: "https://status.artquarell.com",
  rateUpUrl: "https://status.artquarell.com",
  rateDownUrl: "https://status.artquarell.com",
  supportMessageUrl: "https://status.artquarell.com",
  uploadAlternativesUrl: "https://status.artquarell.com",
  noAlternativesUrl: "https://status.artquarell.com",
  couponCode: "TESTCODE5",
  editUrl: "https://artquarell.com/products/beispiel?orderEdit=TEST",
  expiresHours: "96",
  subject: "Test-Betreff (Team-Text)",
  message: "<p>Test-Nachricht — hier steht im Ernstfall der im Dialog verfasste Team-Text.</p>",
  // Gutschrift nach Bestelländerung
  giftCardCode: "ABCD EFGH IJKL MNOP",
  amount: "20,00 €",
  expiresOn: "20.10.2028",
  summary: "Leinwand 75×50 cm → Poster 30×20 cm",
  shopUrl: "https://artquarell.com",
  brandName: "Artquarell.com",
  supportEmail: "du@artquarell.com",
  logoUrl: "https://artquarell.com/cdn/shop/files/logo_new_aq.png?v=1785858513&width=600",
  noLogo: false,
};

// Mini-Renderer — 1:1-Spiegel von AbandonedCheckout/templateRenderer.js
const renderConditionals = (html, data) =>
  html.replace(/\{\{#if\s+([a-zA-Z0-9_.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, key, body) => (data[key] ? body : ""));
const renderData = (html, data) =>
  html.replace(/\{\{([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = data[key];
    return v === undefined || v === null ? "" : String(v);
  });
const renderTexts = (html, texts) =>
  html.replace(/\{\{t\.([a-zA-Z0-9_]+)\}\}/g, (_, key) => {
    const v = texts?.[key];
    return v === undefined || v === null ? "" : String(v);
  });

const extractPlaceholders = (s) => (String(s || "").match(/\{\{[a-zA-Z0-9_]+\}\}/g) || []).sort().join(",");

const renderMail = (template, texts, store, lang) => {
  if (!template?.html) return { html: "", subject: "" };
  const merged = {
    ...texts,
    ...SAMPLE_DATA,
    brandName: store.brandName,
    supportEmail: store.supportEmail,
    logoUrl: store.logoUrl,
    noLogo: !store.logoUrl,
    langCode: lang,
  };
  let html = renderTexts(String(template.html), texts);
  html = renderConditionals(html, merged);
  html = renderData(html, merged);
  const subject = renderData(renderConditionals(String(texts.subject || ""), merged), merged);
  return { html, subject };
};

export default {
  name: "MailTemplatesPanel",
  data() {
    return {
      templates: {},
      view: "grid",
      selectedTemplateId: null,
      selectedStoreKey: "AQ_DE",
      selectedLang: "de",
      testOrderRef: "",
      draft: {},
      loading: true,
      saving: false,
      sendingTestId: null,
      unsubscribe: null,
    };
  },
  computed: {
    langItems() {
      return LANGS;
    },
    storeItems() {
      return STORES.map((s) => ({ value: s.key, text: s.text }));
    },
    selectedStore() {
      return STORES.find((s) => s.key === this.selectedStoreKey) || STORES[0];
    },
    // Karten: feste Reihenfolge aus TEMPLATE_META, Rest alphabetisch; Vollständigkeit über alle Sprachen
    templateList() {
      const ids = Object.keys(this.templates).sort((a, b) => {
        const ia = TEMPLATE_ORDER.indexOf(a);
        const ib = TEMPLATE_ORDER.indexOf(b);
        if (ia !== -1 || ib !== -1) return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        return a.localeCompare(b);
      });
      return ids.map((id) => {
        const tpl = this.templates[id];
        const keys = Object.keys(tpl.texts?.de || {});
        const missingLangs = LANGS.filter(({ value }) => keys.some((k) => !String(tpl.texts?.[value]?.[k] || "").trim())).length;
        return { id, ...this.meta(id), missingLangs, updatedAt: tpl.updatedAt || null, raw: tpl };
      });
    },
    template() {
      return this.selectedTemplateId ? this.templates[this.selectedTemplateId] || null : null;
    },
    deTexts() {
      return this.template?.texts?.de || {};
    },
    textKeys() {
      // Reihenfolge: subject zuerst, dann wie im deutschen Original
      const keys = Object.keys(this.deTexts);
      return ["subject", ...keys.filter((k) => k !== "subject")].filter((k) => keys.includes(k));
    },
    savedLangTexts() {
      return this.template?.texts?.[this.selectedLang] || {};
    },
    isDirty() {
      return this.textKeys.some((k) => (this.draft[k] || "") !== (this.savedLangTexts[k] || ""));
    },
    // Fehlende/leere Keys je Sprache (gegen das deutsche Key-Set)
    completeness() {
      const out = {};
      const keys = Object.keys(this.deTexts);
      for (const { value: lang } of LANGS) {
        const t = this.template?.texts?.[lang] || {};
        out[lang] = keys.filter((k) => !String(t[k] || "").trim()).length;
      }
      return out;
    },
    previewRendered() {
      return renderMail(this.template, { ...this.savedLangTexts, ...this.draft }, this.selectedStore, this.selectedLang);
    },
    previewHtml() {
      return this.previewRendered.html;
    },
    previewSubject() {
      return this.previewRendered.subject;
    },
  },
  watch: {
    // Store-Wechsel springt auf dessen Standardsprache — das ist die Sicht,
    // die die meisten Kunden dieses Stores bekommen
    selectedStoreKey() {
      if (this.view === "edit") this.switchLang(this.selectedStore.defaultLang);
    },
    savedLangTexts() {
      // Fremd-Update (anderer Tab/Seed) nur übernehmen, wenn lokal nichts editiert ist
      if (!this.isDirty) this.resetDraft();
    },
  },
  created() {
    this.unsubscribe = onSnapshot(collection(db, "transactionalMailTemplates"), (snap) => {
      const next = {};
      snap.forEach((d) => {
        next[d.id] = d.data();
      });
      this.templates = next;
      this.loading = false;
    });
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    meta(id) {
      const m = TEMPLATE_META[id] || {};
      const tpl = this.templates[id] || {};
      return {
        name: m.name || tpl.name || id,
        description: m.description || tpl.description || "",
        icon: m.icon || "mdi-email-outline",
      };
    },
    langLabel(code) {
      return LANG_NAMES[code] || code;
    },
    shortDate(iso) {
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit" });
    },
    // Kartenvorschau: Texte in der Standardsprache des gewählten Stores (Fallback Deutsch)
    cardHtml(tpl) {
      const lang = tpl.raw.texts?.[this.selectedStore.defaultLang] ? this.selectedStore.defaultLang : "de";
      return renderMail(tpl.raw, tpl.raw.texts?.[lang] || {}, this.selectedStore, lang).html;
    },
    openEditor(id) {
      this.selectedTemplateId = id;
      this.selectedLang = this.templates[id]?.texts?.[this.selectedStore.defaultLang] ? this.selectedStore.defaultLang : "de";
      this.resetDraft();
      this.view = "edit";
    },
    backToGrid() {
      if (this.isDirty && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
      this.view = "grid";
      this.selectedTemplateId = null;
      this.draft = {};
    },
    switchLang(lang) {
      if (lang === this.selectedLang) return;
      if (this.isDirty && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
      this.selectedLang = lang;
      this.resetDraft();
    },
    rowsFor(key) {
      return String(this.deTexts[key] || "").length > 90 ? 3 : 1;
    },
    resetDraft() {
      const d = {};
      for (const k of this.textKeys) d[k] = this.savedLangTexts[k] || "";
      this.draft = d;
    },
    placeholderMismatch(key) {
      if (this.selectedLang === "de") return false;
      return extractPlaceholders(this.draft[key]) !== extractPlaceholders(this.deTexts[key]);
    },
    async saveLang() {
      this.saving = true;
      try {
        const clean = {};
        for (const k of this.textKeys) clean[k] = this.draft[k] || "";
        await updateDoc(doc(db, "transactionalMailTemplates", this.selectedTemplateId), {
          [`texts.${this.selectedLang}`]: clean,
          updatedAt: new Date().toISOString(),
        });
        EventBus.$emit(events.SNACKBAR, {
          message: `${this.langLabel(this.selectedLang)} gespeichert`,
          type: "success",
        });
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Speichern fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.saving = false;
      }
    },
    // Aus der Karte: Standardsprache des Stores; im Editor: gewählte Sprache (ungespeichertes wird vorher gespeichert)
    async sendTestMail(templateId) {
      this.sendingTestId = templateId;
      try {
        const inEditor = this.view === "edit" && templateId === this.selectedTemplateId;
        if (inEditor && this.isDirty) {
          await this.saveLang();
        }
        const lang = inEditor ? this.selectedLang : this.selectedStore.defaultLang;
        const call = httpsCallable(fbFunctions, "sendTransactionalTestMail");
        const res = await call({
          templateId,
          lang,
          shopId: this.selectedStore.shopId,
          countryCode: this.selectedStore.countryCode,
          // Mit Order-Referenz kommen Sprache/Store/Kunde/Bilder aus der
          // echten Bestellung — Sprach-/Store-Auswahl oben wird dann ignoriert
          orderRef: inEditor ? (this.testOrderRef || "").trim() || undefined : undefined,
        });
        if (res?.data?.status === "ok") {
          const src = res.data.order ? `Bestellung ${res.data.order}` : res.data.store;
          EventBus.$emit(events.SNACKBAR, { message: `Testmail ${src} (${res.data.lang}) an ${res.data.email} gesendet`, type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${res?.data?.message}`, type: "error" });
        }
      } catch (e) {
        EventBus.$emit(events.SNACKBAR, { message: `Testversand fehlgeschlagen: ${e.message}`, type: "error" });
      } finally {
        this.sendingTestId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$accent: #7b5cd6;

.mtp {
  padding: 4px 2px 24px;
  color: #1d1d28;
}

/* ── Toolbar ── */
.mtp-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;

  &__title {
    font-size: 19px;
    font-weight: 700;
    letter-spacing: -0.2px;
  }
  &__sub {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.5);
  }
  &__icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: rgba(123, 92, 214, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    .v-icon { color: $accent; }
  }
}
.mtp-back {
  margin-left: -6px;
}
.mtp-select {
  max-width: 280px;

  &--store {
    max-width: 210px;
  }
  &--order {
    max-width: 185px;
  }
  &--store,
  &--order {
    ::v-deep .v-input__icon--prepend-inner .v-icon {
      color: #7b5cd6;
      font-size: 18px;
    }
  }
  ::v-deep .v-input__slot {
    background: #fff !important;
    border-radius: 10px;
    box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08) !important;
    fieldset { border-color: transparent; }
  }
}
.mtp-btn {
  text-transform: none;
  font-weight: 700;
  letter-spacing: 0.1px;
}
.mtp-empty {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
  padding: 24px 4px;
  code {
    background: #edeafa;
    color: #6a4fc4;
    border-radius: 6px;
    padding: 1px 6px;
  }
}

/* ── Galerie (Vorbild: E-Mail-Marketing → Templates) ── */
.mtp-gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
}
.mtp-tplcard {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.07);
  transition: box-shadow 0.15s, border-color 0.15s;

  &:hover {
    box-shadow: 0 4px 16px rgba(20, 20, 40, 0.12);
    .mtp-tplcard__hover { opacity: 1; }
  }
  &--incomplete {
    border-color: rgba(239, 83, 80, 0.35);
  }

  &__preview {
    position: relative;
    height: 250px;
    overflow: hidden;
    background: #f3f2f7;
    cursor: pointer;
  }
  // 600 px breite Mail, auf Kartenbreite skaliert und zentriert; keine Interaktion mit dem Inhalt
  &__frame {
    position: absolute;
    left: 50%;
    top: 0;
    width: 600px;
    height: 620px;
    border: 0;
    transform: translateX(-50%) scale(0.46);
    transform-origin: top center;
    pointer-events: none;
    background: #fff;
  }
  &__hover {
    position: absolute;
    inset: 0;
    background: rgba(30, 30, 42, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.15s;
  }
  &__meta {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    flex: 1;
  }
  &__icon {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: rgba(123, 92, 214, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    .v-icon { color: $accent; }
  }
  &__text {
    flex: 1;
    min-width: 0;
  }
  &__name {
    font-size: 13.5px;
    font-weight: 700;
    line-height: 1.3;
  }
  &__desc {
    font-size: 12px;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.55);
    margin-top: 2px;
  }
  &__badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
  }
  &__actions {
    display: flex;
    align-items: center;
    padding: 2px 6px 6px;
    .v-btn { text-transform: none; letter-spacing: 0; }
  }
}
.mtp-badge {
  font-size: 10.5px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  background: rgba(0, 0, 0, 0.05);
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;

  &--ok {
    color: #2e7d32;
    background: #e8f5e9;
  }
  &--warn {
    color: #c62828;
    background: #fdecea;
  }
}

/* ── Sprach-Pills ── */
.mtp-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.mtp-lang {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  outline: none;
  cursor: pointer;
  background: #fff;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.62);
  box-shadow: 0 1px 4px rgba(20, 20, 40, 0.08);
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(20, 20, 40, 0.13);
  }
  &--active {
    background: $accent;
    color: #fff;
    box-shadow: 0 4px 12px rgba(123, 92, 214, 0.35);
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;

    &--ok { background: #4caf50; }
    &--missing { background: #ef5350; }
  }
  &--active &__dot--ok { background: #b5f0b8; }
  &--active &__dot--missing { background: #ffc4c1; }

  &__count {
    background: #fdecea;
    color: #c62828;
    border-radius: 999px;
    font-size: 10.5px;
    padding: 1px 6px;
  }
  &--active &__count {
    background: rgba(255, 255, 255, 0.22);
    color: #fff;
  }
}

/* ── Layout: Editor + Vorschau ── */
.mtp-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
}
.mtp-card {
  padding: 18px 20px;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    min-height: 28px;
  }
  &__title {
    font-size: 14.5px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }
  &__icon {
    color: $accent !important;
  }
}
.mtp-muted {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: auto;
}

/* ── Key-Editor ── */
.mtp-key {
  margin-bottom: 14px;

  &__label {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;

    code {
      background: #edeafa;
      color: #6a4fc4;
      font-size: 11px;
      font-weight: 700;
      border-radius: 6px;
      padding: 2px 8px;
    }
  }
  &__input {
    ::v-deep .v-input__slot {
      border-radius: 10px;
      background: #fff !important;
      fieldset { border-color: rgba(0, 0, 0, 0.12); }
    }
    ::v-deep textarea {
      font-size: 13.5px;
      line-height: 1.5;
    }
    &--empty ::v-deep .v-input__slot {
      background: #fdecea !important;
      fieldset { border-color: #ef9a9a; }
    }
  }
  &__reference {
    font-size: 12px;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.42);
    margin-top: 4px;
    padding-left: 2px;
    white-space: pre-wrap;
  }
}
.mtp-warn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #fff3e0;
  color: #b26a00;
  font-size: 10.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 9px;
}

/* ── Vorschau ── */
.mtp-card--preview {
  position: sticky;
  top: 16px;
  align-self: start;
}
.mtp-subject {
  margin-left: auto;
  max-width: 60%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #edeafa;
  color: #6a4fc4;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 12px;
}
.mtp-preview {
  width: 100%;
  height: 76vh;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background: #fff;
}
</style>
